import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, of } from 'rxjs';

import {
	AddDevicesToGatewayResult,
	AddDeviceToGatewayStatus,
	CloudGatewayDiscoveredDevice,
	CloudGatewayDiscoveryStatus,
	RemoveDeviceFromGatewayStatus,
	RemoveDevicesFromGatewayResult
} from '@shure/cloud/shared/models/devices';
import { ILogger } from '@shure/shared/angular/utils/logging';

import { CloudGatewayApiService, DiscoveryResult } from '../api/cloud-gateway-api.service';

import {
	AddDevicesToGatewayGQL,
	CloudGatewayDeviceDiscoveryStatus,
	GatewayDiscoveredDevicesSubscriptionGQL,
	RemoveDevicesFromGatewayGQL
} from './graphql/generated/cloud-sys-api';
import { mapAddDevicesToGatewayResultFromSysApi } from './mappers/map-add-devices-to-gateway-result';
import { mapCloudGatewayDiscoveredDeviceFromSysApi } from './mappers/map-cloud-gateway-discovered-device';
import { mapCloudGatewayDiscoveryStatusFromSysApi } from './mappers/map-cloud-gateway-discovery-status';
import { mapRemoveDevicesFromGatewayResultFromSysApi } from './mappers/map-remove-devices-from-gateway-result';

@Injectable({ providedIn: 'root' })
export class SysApiCloudGatewayApiService extends CloudGatewayApiService {
	private readonly logger: ILogger;

	constructor(
		logger: ILogger,
		private readonly gatewayDiscoveredDevicesSubscription: GatewayDiscoveredDevicesSubscriptionGQL,
		private readonly addDevicesToGatewayMutation: AddDevicesToGatewayGQL,
		private readonly removeDevicesFromGatewayMutation: RemoveDevicesFromGatewayGQL
	) {
		super();
		this.logger = logger.createScopedLogger('SysApiCloudGatewayApiService');
	}

	/**
	 * Discovery is implemented as a subscription that emits a value for every device discovered along
	 * with a terminal value when discovery is complete.
	 */
	public override scanDiscoveredDevicesForGateway$(gatewayId: string): Observable<DiscoveryResult> {
		const discoveredDevices: CloudGatewayDiscoveredDevice[] = [];
		const discoveryResultSubject = new BehaviorSubject<DiscoveryResult>({
			status: CloudGatewayDiscoveryStatus.InProgress,
			discoveredDevices: []
		});

		this.gatewayDiscoveredDevicesSubscription.subscribe({ gatewayId }).subscribe({
			next: (value) => {
				const deviceOrResult = value?.data?.cloudGatewayDiscoveredDevices;
				if (!deviceOrResult) {
					return;
				}

				if (deviceOrResult.__typename === 'CloudGatewayDiscoveredDevice') {
					discoveredDevices.push(mapCloudGatewayDiscoveredDeviceFromSysApi(deviceOrResult));
					discoveryResultSubject.next({
						status: CloudGatewayDiscoveryStatus.InProgress,
						discoveredDevices
					});
				} else {
					if (deviceOrResult.status === CloudGatewayDeviceDiscoveryStatus.DiscoveryComplete) {
						discoveryResultSubject.next({
							status: CloudGatewayDiscoveryStatus.Complete,
							discoveredDevices
						});
					} else {
						discoveryResultSubject.next({
							status: mapCloudGatewayDiscoveryStatusFromSysApi(deviceOrResult.status),
							discoveredDevices
						});
					}

					discoveryResultSubject.complete();
				}
			}
		});

		return discoveryResultSubject;
	}

	public override addDevices$(gatewayId: string, deviceHardwareIds: string[]): Observable<AddDevicesToGatewayResult> {
		return this.addDevicesToGatewayMutation
			.mutate(
				{
					input: {
						cloudGatewayId: gatewayId,
						hardwareIds: deviceHardwareIds
					}
				},
				{ useMutationLoading: true }
			)
			.pipe(
				map((mutationResult) => {
					if (mutationResult.loading) {
						return {
							devices: deviceHardwareIds.map((hardwareId) => ({
								hardwareId,
								status: AddDeviceToGatewayStatus.InProgress
							}))
						};
					}

					return mutationResult.data
						? mapAddDevicesToGatewayResultFromSysApi(mutationResult.data)
						: { devices: [] };
				}),
				catchError((error) => {
					this.logger.error('addDevices$', 'Error', error);
					return of({
						devices: deviceHardwareIds.map((hardwareId) => ({
							hardwareId,
							status: AddDeviceToGatewayStatus.GenericFailure
						}))
					});
				})
			);
	}

	public override removeDevices$(
		gatewayId: string,
		deviceHardwareIds: string[]
	): Observable<RemoveDevicesFromGatewayResult> {
		return this.removeDevicesFromGatewayMutation
			.mutate(
				{
					input: {
						cloudGatewayId: gatewayId,
						hardwareIds: deviceHardwareIds
					}
				},
				{ useMutationLoading: true }
			)
			.pipe(
				map((mutationResult) => {
					if (mutationResult.loading) {
						return {
							devices: deviceHardwareIds.map((hardwareId) => ({
								hardwareId,
								status: RemoveDeviceFromGatewayStatus.InProgress
							}))
						};
					}

					return mutationResult.data
						? mapRemoveDevicesFromGatewayResultFromSysApi(mutationResult.data)
						: { devices: [] };
				}),
				catchError((error) => {
					this.logger.error('removeDevices$', 'Error', error);
					return of({
						devices: deviceHardwareIds.map((hardwareId) => ({
							hardwareId,
							status: RemoveDeviceFromGatewayStatus.GenericFailure
						}))
					});
				})
			);
	}
}
